import _defineProperty from '@babel/runtime/helpers/defineProperty';
import React__default from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

const BASE_CLASS = 'nui-collapse';
const Classes = {
  BASE: BASE_CLASS,
  BODY: `${BASE_CLASS}-body`
};

/**
 * `Collapse` can be in one of six states, enumerated here.
 * When changing the `isOpen` prop, the following happens to the states:
 * isOpen={true}  : CLOSED -> OPEN_START -> OPENING -> OPEN
 * isOpen={false} : OPEN -> CLOSING_START -> CLOSING -> CLOSED
 */
const AnimationStates = {
  /**
     * The body is re-rendered, height is set to the measured body height and
     * the body Y is set to 0.
     */
  OPEN_START: 'OPEN_START',
  /**
     * Animation begins, height is set to auto. This is all animated, and on
     * complete, the state changes to OPEN.
     */
  OPENING: 'OPENING',
  /**
     * The collapse height is set to auto, and the body Y is set to 0 (so the
     * element can be seen as normal).
     */
  OPEN: 'OPEN',
  /**
     * Height has been changed from auto to the measured height of the body to
     * prepare for the closing animation in CLOSING.
     */
  CLOSING_START: 'CLOSING_START',
  /**
     * Height is set to 0 and the body Y is at -height. Both of these properties
     * are transformed, and then after the animation is complete, the state
     * changes to CLOSED.
     */
  CLOSING: 'CLOSING',
  /**
     * The contents of the collapse is not rendered, the collapse height is 0,
     * and the body Y is at -height (so that the bottom of the body is at Y=0).
     */
  CLOSED: 'CLOSED'
};
const collapsePropTypes = {
  /** Optional custom className */
  className: PropTypes.string,
  /** Content that goes inside collapse */
  children: PropTypes.node,
  /** Whether the content of the component is open or closed. */
  isOpen: PropTypes.bool,
  /** Whether the child components will remain mounted when the Collapse is closed. Setting to true may improve
   *  performance by avoiding re-mounting children. */
  keepChildrenMounted: PropTypes.bool,
  /**  The length of time the transition takes, in milliseconds. This must match the duration of the animation in CSS. */
  transitionDuration: PropTypes.number
};
class Collapse extends React__default.Component {
  // The element containing the contents of the collapse.

  constructor(props) {
    super(props);
    _defineProperty(this, "contents", void 0);
    _defineProperty(this, "timeoutIds", []);
    _defineProperty(this, "clearTimeouts", () => {
      this.timeoutIds.forEach(id => {
        window.clearTimeout(id);
      });
      this.timeoutIds = [];
    });
    _defineProperty(this, "contentsRefHandler", el => {
      this.contents = el;
      if (this.contents != null) {
        const height = this.contents.clientHeight;
        this.setState({
          animationState: this.props.isOpen ? AnimationStates.OPEN : AnimationStates.CLOSED,
          height: height === 0 ? undefined : `${height}px`,
          heightWhenOpen: height === 0 ? undefined : height
        });
      }
    });
    this.state = {
      animationState: this.props.isOpen ? AnimationStates.OPEN : AnimationStates.CLOSED,
      height: undefined,
      heightWhenOpen: undefined
    };
  }
  static getDerivedStateFromProps(props, state) {
    const {
      isOpen
    } = props;
    const {
      animationState
    } = state;
    if (isOpen) {
      switch (animationState) {
        case AnimationStates.OPEN:
          break;
        case AnimationStates.OPENING:
          // allow Collapse#onDelayedStateChange() to handle the transition here
          break;
        default:
          return {
            animationState: AnimationStates.OPEN_START
          };
      }
    } else {
      switch (animationState) {
        case AnimationStates.CLOSED:
          break;
        case AnimationStates.CLOSING:
          // allow Collapse#onDelayedStateChange() to handle the transition here
          break;
        default:
          // need to set an explicit height so that transition can work
          return {
            animationState: AnimationStates.CLOSING_START,
            height: `${state.heightWhenOpen}px`
          };
      }
    }
    return null;
  }
  componentDidMount() {
    this.forceUpdate();
    // HACKHACK: this should probably be done in getSnapshotBeforeUpdate
    if (this.props.isOpen) {
      this.setState({
        animationState: AnimationStates.OPEN,
        height: 'auto'
      });
    } else {
      this.setState({
        animationState: AnimationStates.CLOSED,
        height: '0px'
      });
    }
  }
  componentDidUpdate() {
    if (this.contents == null) {
      return;
    }
    const {
      transitionDuration = 200
    } = this.props;
    const {
      animationState
    } = this.state;
    if (animationState === AnimationStates.OPEN_START) {
      const {
        clientHeight
      } = this.contents;
      this.setState({
        animationState: AnimationStates.OPENING,
        height: `${clientHeight}px`,
        heightWhenOpen: clientHeight
      });
      this.setTimeout(() => this.onDelayedStateChange(), transitionDuration);
    } else if (animationState === AnimationStates.CLOSING_START) {
      const {
        clientHeight
      } = this.contents;
      requestAnimationFrame(() => this.setState({
        animationState: AnimationStates.CLOSING,
        height: '0px',
        heightWhenOpen: clientHeight
      }));
      this.setTimeout(() => this.onDelayedStateChange(), transitionDuration);
    }
  }
  componentWillUnmount() {
    this.clearTimeouts();
  }
  onDelayedStateChange() {
    switch (this.state.animationState) {
      case AnimationStates.OPENING:
        this.setState({
          animationState: AnimationStates.OPEN,
          height: 'auto'
        });
        break;
      case AnimationStates.CLOSING:
        this.setState({
          animationState: AnimationStates.CLOSED
        });
        break;
    }
  }

  /**
     * Set a timeout and remember its ID.
     * All pending timeouts will be cleared when component unmounts.
     *
     * @returns a "cancel" function that will clear timeout when invoked.
     */
  setTimeout(callback, timeout) {
    const handle = window.setTimeout(callback, timeout);
    this.timeoutIds.push(handle);
    return () => window.clearTimeout(handle);
  }

  /**
   * Clear all known timeouts.
   */

  render() {
    const isContentVisible = this.state.animationState !== AnimationStates.CLOSED;
    const shouldRenderChildren = isContentVisible || this.props.keepChildrenMounted;
    const displayWithTransform = isContentVisible && this.state.animationState !== AnimationStates.CLOSING;
    const isAutoHeight = this.state.height === 'auto';
    const containerStyle = {
      height: isContentVisible ? this.state.height : undefined,
      overflowY: isAutoHeight ? 'visible' : undefined,
      // transitions don't work with height: auto
      transition: isAutoHeight ? 'none' : undefined
    };
    const contentsStyle = {
      // only use heightWhenOpen while closing
      transform: displayWithTransform ? 'translateY(0)' : `translateY(-${this.state.heightWhenOpen}px)`,
      // transitions don't work with height: auto
      transition: isAutoHeight ? 'none' : undefined
    };
    return /*#__PURE__*/React__default.createElement("div", {
      className: cx(Classes.BASE, this.props.className),
      style: containerStyle
    }, /*#__PURE__*/React__default.createElement("div", {
      className: Classes.BODY,
      ref: this.contentsRefHandler,
      style: contentsStyle,
      "aria-hidden": !isContentVisible
    }, shouldRenderChildren ? this.props.children : null));
  }
}
_defineProperty(Collapse, "displayName", 'NuiCollapse');
_defineProperty(Collapse, "propTypes", collapsePropTypes);
_defineProperty(Collapse, "defaultProps", {
  children: null,
  className: '',
  isOpen: false,
  keepChildrenMounted: false
});
var Collapse$1 = Collapse;

export { AnimationStates, Classes, Collapse$1 as default };
